if (!customElements.get('lore-category-scroll')) {
  customElements.define('lore-category-scroll', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.grid = this.querySelector('.resource-list--grid');
      this.more = this.querySelector('[data-lore-more]');
      this.link = this.more?.querySelector('[data-lore-next]');
      this.status = this.more?.querySelector('[data-lore-status]');
      if (!this.grid || !this.link) return;
      this.busy = false;
      this.link.addEventListener('click', event => {
        if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.load();
      }, { signal: this.controller.signal });
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.load();
      }, { rootMargin: '400px 0px' });
      this.observer.observe(this.more);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }

    prepareItem(item, page, index) {
      const clone = document.importNode(item, true);
      const nodes = [clone, ...clone.querySelectorAll('*')];
      const ids = new Map();
      nodes.forEach(node => {
        if (!node.id) return;
        const old = node.id;
        const next = `${old}--lore-page-${page}-${index}`;
        ids.set(old, next);
        node.id = next;
      });
      nodes.forEach(node => {
        ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns', 'aria-details', 'aria-activedescendant', 'headers'].forEach(attribute => {
          const value = node.getAttribute(attribute);
          if (value) node.setAttribute(attribute, value.split(/\s+/).map(id => ids.get(id) || id).join(' '));
        });
        ['href', 'xlink:href'].forEach(attribute => {
          const value = node.getAttribute(attribute);
          if (value?.startsWith('#') && ids.has(value.slice(1))) node.setAttribute(attribute, '#' + ids.get(value.slice(1)));
        });
        ['fill', 'stroke', 'clip-path', 'mask', 'filter', 'style'].forEach(attribute => {
          let value = node.getAttribute(attribute);
          if (!value) return;
          ids.forEach((next, old) => { value = value.replaceAll(`url(#${old})`, `url(#${next})`); });
          node.setAttribute(attribute, value);
        });
      });
      return clone;
    }

    async load() {
      if (this.busy || !this.link || this.link.hidden) return;
      this.busy = true;
      this.link.setAttribute('aria-disabled', 'true');
      this.grid.setAttribute('aria-busy', 'true');
      this.status.textContent = this.more.dataset.loadingText;
      this.observer?.unobserve(this.more);
      try {
        const url = new URL(this.link.href, window.location.href);
        url.hash = '';
        const current = new URL(window.location.href);
        for (const key of new Set(current.searchParams.keys())) {
          if (key === this.dataset.pageParam || key === 'section_id' || url.searchParams.has(key)) continue;
          current.searchParams.getAll(key).forEach(value => url.searchParams.append(key, value));
        }
        const expectedPage = Number(url.searchParams.get(this.dataset.pageParam));
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Section request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = html.querySelector('lore-category-scroll');
        if (!result || Number(result.dataset.currentPage) !== expectedPage) throw new Error('Wrong page returned');
        const grid = result.querySelector('.resource-list--grid');
        if (!grid) throw new Error('Product grid missing');
        const seen = new Set(Array.from(this.grid.querySelectorAll('product-card')).map(card => card.dataset.productId));
        const items = Array.from(grid.children).filter(item => {
          if (!item.classList.contains('resource-list__item')) return false;
          const id = item.querySelector('product-card')?.dataset.productId;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        if (!items.length) throw new Error('No new products returned');
        this.grid.append(...items.map((item, index) => this.prepareItem(item, expectedPage, index)));
        const next = result.querySelector('[data-lore-next]');
        if (next) {
          const nextUrl = new URL(next.getAttribute('href'), url);
          nextUrl.searchParams.delete('section_id');
          this.link.href = nextUrl.href;
          this.status.textContent = this.more.dataset.loadedText;
        } else {
          this.link.hidden = true;
          this.status.textContent = '';
        }
        this.dataset.currentPage = String(expectedPage);
        this.failed = false;
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.failed = true;
        this.status.textContent = this.more.dataset.errorText;
      } finally {
        this.busy = false;
        this.link.removeAttribute('aria-disabled');
        this.grid.removeAttribute('aria-busy');
        if (!this.failed && !this.link.hidden && this.isConnected) this.observer?.observe(this.more);
      }
    }
  });
}


